import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  IonButton,
  IonIcon,
  IonText,
  IonRippleEffect,
  IonSpinner,
  IonRefresher,
  IonRefresherContent,
  IonContent,
  IonPage,
  IonFooter,
  IonButtons,
  IonSkeletonText,
} from '@ionic/react';
import { sunny, moon, chatbubbles, settings, addOutline, pricetagOutline } from 'ionicons/icons';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { chatApi } from '../services/chatApi';
import type { ChatSession } from '../services/chatApi';
import { formatDistanceToNow } from 'date-fns';
import HeaderLogo from '../components/common/HeaderLogo';
import AppHeader from '../components/common/AppHeader';
import SmartDocsLogo from '../assets/SmartDocsTextLogoDark.png';
import { PageTitle } from '../components/common/PageTitle';
import { useMarketingCampaign } from '../hooks/useMarketingCampaign';
import { Banner } from '../components/Marketing/Banner';

const PreviousChatsHeader = styled.div`
  display: flex;
  align-items: center;
  margin: 24px 0 16px;
  margin-left: ${props => props.theme?.token?.marginHorizontal || '16px'};
  margin-right: ${props => props.theme?.token?.marginHorizontal || '16px'};
  color: ${props => props.theme?.token?.colorTextSecondary};
  font-weight: 500;

  ion-icon {
    margin-right: 8px;
    color: ${props => props.theme?.token?.colorIcon};
  }
`;

const ChatListContainer = styled.div`
  padding: 0 16px;
  padding-bottom: calc(128px + var(--ion-safe-area-bottom, 0px));
`;

const ChatItem = styled.div`
  padding: 16px;
  margin-bottom: 8px;
  background: ${props => props.theme?.token?.colorBgContainer};
  border: 1px solid ${props => props.theme?.token?.colorBorder};
  border-radius: 8px;
  cursor: pointer;
  color: ${props => props.theme?.token?.colorText};
  transition: background-color 0.2s ease;
  box-shadow: 0 2px 8px ${props => props.theme?.token?.colorBorder};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  overflow: hidden;

  &:hover {
    background: ${props => props.theme?.token?.colorBgTextHover};
  }
`;

const ChatContent = styled.div`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 70%;
  color: ${props => props.theme?.token?.colorTextTertiary};
  font-weight: 600;
  font-style: normal;
`;

const ChatTime = styled.span`
  color: ${props => props.theme?.token?.colorTextTertiary};
  font-size: 14px;
  margin-left: 16px;
`;

// Removed custom Footer and NewChatButton - using Ionic's IonFooter, IonToolbar, and IonFab instead


const StyledButton = styled(IonButton)`
  --color: ${props => props.theme?.token?.colorIcon};
  --background: transparent;
  --background-hover: rgba(0, 0, 0, 0.05);
  --background-activated: rgba(0, 0, 0, 0.1);
  --padding-start: 8px;
  --padding-end: 8px;
  margin: 0;
  height: 44px;
  width: 44px;

  ion-icon {
    color: ${props => props.theme?.token?.colorIcon};
    font-size: 20px;
  }
`;

const LoadingSkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LoadingSkeletonCard = styled.div`
  border-radius: 8px;
  padding: 16px;
  border: 1px solid ${props => props.theme?.token?.colorBorder};
  background: ${props => props.theme?.token?.colorBgContainer};
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 40px 0;
  color: ${props => props.theme?.token?.colorTextSecondary};
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: ${props => props.theme?.token?.colorTextTertiary};
  margin: -16px 16px 24px;
  line-height: 1.4;
`;

const WelcomeTitle = styled(IonText)`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.3;
  margin: 32px 16px;
  color: ${(props: { theme?: { token?: { colorTextSecondary?: string } } }) =>
    props.theme?.token?.colorTextSecondary};
  display: block;
`;

const WelcomeCenterContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px 32px 8px;
  box-sizing: border-box;
`;

const WelcomeLogo = styled.img`
  width: 160px;
  height: auto;
  margin-bottom: 16px;
  background: transparent;
`;

const WelcomeGreeting = styled.h2`
  font-size: 22px;
  font-weight: 600;
  color: ${props => props.theme?.token?.colorTextSecondary};
  margin: 0;
`;

const WelcomeCard = styled.div`
  margin: 0 auto 16px;
  max-width: 320px;
  padding: 16px;
  background: ${props => props.theme?.token?.colorBgContainer};
  border: 1px solid ${props => props.theme?.token?.colorBorder};
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  position: relative;

  /* Tooltip arrow pointing down */
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid ${props => props.theme?.token?.colorBgContainer};
  }
  &::before {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 9px solid transparent;
    border-right: 9px solid transparent;
    border-top: 9px solid rgba(0, 0, 0, 0.1);
  }
`;

const WelcomeCardText = styled.p`
  font-size: 14px;
  color: ${props => props.theme?.token?.colorText};
  line-height: 1.5;
  margin: 0;
`;

const WelcomeCardCta = styled.p`
  font-size: 14px;
  color: ${props => props.theme?.token?.colorTextSecondary};
  font-weight: 600;
  margin: 12px 0 0;
`;

const NewChatButtonLarge = styled(IonButton)`
  margin: 10px 16px 10px;
  --border-radius: 10px;
  height: 48px;
  font-size: 16px;
  font-weight: 600;
`;

const FooterMeta = styled.div`
  background: ${props => props.theme?.token?.colorBgContainer};
  border-top: 1px solid ${props => props.theme?.token?.colorBorder};
  box-shadow: 0 -2px 10px rgba(15, 23, 42, 0.06);
`;

const FixedActionFooter = styled(IonFooter)`
  background: ${props => props.theme?.token?.colorBgContainer};
  border: none;
  box-shadow: 0 -8px 20px rgba(15, 23, 42, 0.06);
  padding: 0 0 var(--ion-safe-area-bottom, 0px);
  z-index: 20;
`;

interface ChatListPageProps {
  isDarkMode: boolean;
  onThemeChange: (darkMode: boolean) => void;
}

const DEFAULT_DISTRIBUTOR = "SmartDocs Distributor";
const DEFAULT_REGION = "North America";

const getContractorName = () => {
  const email = localStorage.getItem('user_email');
  if (email) {
    return email.split('@')[0] || email;
  }
  return "Contractor";
};

const ChatListPage: React.FC<ChatListPageProps> = ({ isDarkMode, onThemeChange }) => {
  const history = useHistory();
  const { logout, isAuthenticated, user } = useAuth();
  const isSubscriptionUser = user?.role === 'SubscriptionUser';
  const { campaign } = useMarketingCampaign();
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Show banner for banner-type campaigns
  const showBanner = React.useMemo(() => {
    return (
      isAuthenticated &&
      campaign &&
      campaign.communication_type === 'banner' &&
      campaign.message &&
      !bannerDismissed
    );
  }, [isAuthenticated, campaign, bannerDismissed]);

  // Reset banner dismissal when campaign changes
  React.useEffect(() => {
    setBannerDismissed(false);
  }, [campaign?.id]);

  const fetchSessions = async (retryCount = 0) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await chatApi.getUserSessions();
      if (response.success) {
        setSessions(response.data.sessions || []);
      } else {
        setError('Failed to load sessions');
      }
    } catch (err: any) {
      console.error('Error fetching sessions:', err);

      // Check if it's an authentication error
      if (err?.response?.status === 401) {
        // Try to refresh token and retry once
        if (retryCount === 0) {
          try {
            const { authApi } = await import('../services/authApi');
            await authApi.refresh();
            // Retry the fetch with new token
            return fetchSessions(retryCount + 1);
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            setError('Authentication failed. Please log in again.');
            // Redirect to login after a delay
            setTimeout(() => {
              logout();
              history.replace('/signin');
            }, 2000);
            return;
          }
        }
      }

      setError('Failed to load sessions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const handleRefresh = async (event: CustomEvent) => {
    try {
      await fetchSessions();
    } catch (error) {
      console.error('Error during refresh:', error);
    } finally {
      event.detail.complete();
    }
  };

  const handleNewChat = async () => {
    if (isCreatingChat) {
      console.log('[ChatList] Chat creation already in progress');
      return;
    }

    try {
      setIsCreatingChat(true);
      setError(null); // Clear any previous errors

      const distributorName = isSubscriptionUser
        ? undefined
        : (user?.distributor?.name || DEFAULT_DISTRIBUTOR);
      const contractorName = isSubscriptionUser ? undefined : getContractorName();
      const region = isSubscriptionUser ? undefined : DEFAULT_REGION;

      const response = await chatApi.createSession(distributorName, contractorName, region);

      if (response.success) {
        const newSessionId = response.data.session_id;
        const welcomeMessage = response.data.welcome_message;
        console.log('[ChatList] Created new session:', newSessionId);

        history.push(`/chat/${newSessionId}`, {
          welcomeMessage,
          isNewSession: true
        });
      } else {
        console.error('[ChatList] Failed to create new chat:', response);
        setError('Failed to create new chat. Please try again.');
      }
    } catch (error: any) {
      console.error('[ChatList] Error creating new chat:', error);

      if (error?.response?.status === 401) {
        try {
          const { authApi } = await import('../services/authApi');
          await authApi.refresh();
          return handleNewChat();
        } catch (refreshError) {
          console.error('Token refresh failed during chat creation:', refreshError);
          setError('Authentication failed. Please log in again.');
          setTimeout(() => {
            logout();
            history.replace('/signin');
          }, 2000);
          return;
        }
      }

      if (error?.response?.status === 403 && isSubscriptionUser) {
        const apiMessage = error?.response?.data?.message;
        setError(apiMessage || 'Trial expired or limit reached. Subscribe to continue.');
        return;
      }

      setError(error?.message || 'Failed to create new chat. Please try again.');
    } finally {
      setIsCreatingChat(false);
    }
  };

  const handleChatClick = (sessionId: string) => {
    history.push(`/chat/${sessionId}`);
  };

  // Removed search functionality for now - can be re-added later if needed
  const filteredSessions = sessions;

  const formatTime = (dateString: string) => {
    try {
      // Parse the UTC timestamp correctly by ensuring it's treated as UTC
      // If the string doesn't end with 'Z', add it to ensure UTC parsing
      const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
      const utcDate = new Date(utcDateString);
      return formatDistanceToNow(utcDate, { addSuffix: true });
    } catch (error) {
      return 'Unknown time';
    }
  };

  return (
    <IonPage>
      {showBanner && (
        <Banner
          message={campaign.message}
          onDismiss={() => setBannerDismissed(true)}
        />
      )}
      {(isLoading || filteredSessions.length > 0) ? (
        <AppHeader style={showBanner ? { marginTop: '48px' } : {}}>
            <HeaderLogo />
            <IonButtons slot="end">
              <StyledButton onClick={() => onThemeChange(!isDarkMode)}>
                <IonIcon icon={isDarkMode ? sunny : moon} />
              </StyledButton>
              {isSubscriptionUser && (
                <StyledButton onClick={() => history.push('/subscribe')}>
                  <IonIcon icon={pricetagOutline} />
                </StyledButton>
              )}
              <StyledButton onClick={() => history.push('/profile')}>
                <IonIcon icon={settings} />
              </StyledButton>
            </IonButtons>
        </AppHeader>
      ) : null}

      <IonContent forceOverscroll={false}>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        {filteredSessions.length === 0 && !isLoading ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 8px 0' }}>
              <StyledButton onClick={() => onThemeChange(!isDarkMode)}>
                <IonIcon icon={isDarkMode ? sunny : moon} />
              </StyledButton>
              {isSubscriptionUser && (
                <StyledButton onClick={() => history.push('/subscribe')}>
                  <IonIcon icon={pricetagOutline} />
                </StyledButton>
              )}
              <StyledButton onClick={() => history.push('/profile')}>
                <IonIcon icon={settings} />
              </StyledButton>
            </div>
            <WelcomeCenterContainer>
              <WelcomeLogo src={SmartDocsLogo} alt="SmartDocs" />
              <WelcomeGreeting>
                Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
              </WelcomeGreeting>
            </WelcomeCenterContainer>
          </>
        ) : (
          <PageTitle>
            Hi{user?.name ? ` ${user.name.split(' ')[0]}` : ''}
          </PageTitle>
        )}

        {(isLoading || filteredSessions.length > 0 || error) && (
          <>
            <PreviousChatsHeader>
              <IonIcon icon={chatbubbles} />
              Previous Chats
            </PreviousChatsHeader>

            <ChatListContainer>
              {isLoading ? (
                <LoadingSkeletonContainer>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <LoadingSkeletonCard key={`session-skeleton-${index}`}>
                      <IonSkeletonText animated style={{ width: '65%', height: '18px', marginBottom: '8px' }} />
                      <IonSkeletonText animated style={{ width: '30%', height: '14px' }} />
                    </LoadingSkeletonCard>
                  ))}
                </LoadingSkeletonContainer>
              ) : error ? (
                <EmptyStateContainer>
                  <IonIcon icon={chatbubbles} style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <p>{error}</p>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <IonButton size="small" onClick={(e) => { e.preventDefault(); fetchSessions(); }}>Retry</IonButton>
                    {(error.includes('Subscribe') || error.includes('Upgrade') || error.includes('limit')) && (
                      <IonButton size="small" fill="solid" onClick={() => history.push('/subscribe')}>Subscribe</IonButton>
                    )}
                  </div>
                </EmptyStateContainer>
              ) : (
                filteredSessions.map((session) => (
                  <ChatItem
                    key={session.session_id}
                    onClick={() => handleChatClick(session.session_id)}
                    className="ion-activatable ripple-parent"
                  >
                    <ChatContent>{session.session_title || 'New conversation'}</ChatContent>
                    <ChatTime>{formatTime(session.updated_at || session.created_at)}</ChatTime>
                    <IonRippleEffect type="bounded" />
                  </ChatItem>
                ))
              )}
            </ChatListContainer>
          </>
        )}

      </IonContent>

      <FixedActionFooter>
        {filteredSessions.length === 0 && !isLoading && (
          <WelcomeCard>
            <WelcomeCardText>
              I&apos;m {user?.agent_name?.trim() || 'SmartDocs'}, your technical assistant for issues in the field. You can ask me about error codes, parts, troubleshooting, or anything about the units you work with.
            </WelcomeCardText>
            <WelcomeCardCta>
              Tap below to start a new conversation.
            </WelcomeCardCta>
          </WelcomeCard>
        )}
        <NewChatButtonLarge expand="block" onClick={handleNewChat} disabled={isCreatingChat}>
          <IonIcon icon={addOutline} slot="start" />
          {isCreatingChat ? 'Starting...' : 'New Conversation'}
        </NewChatButtonLarge>
        <FooterMeta>
          <div style={{
            textAlign: 'center',
            color: 'var(--ion-color-medium)',
            fontSize: '14px',
            opacity: 0.6,
            padding: '8px 16px'
          }}>
            powered by Smartdocs
          </div>
        </FooterMeta>
      </FixedActionFooter>
    </IonPage>
  );
};

export default ChatListPage;
